// plan-03 S06: idle chatter scheduler — bible §6, §19

import { IDLE_MEAN_S, IDLE_MIN_S, IDLE_MAX_S } from './cadence'
import { IDLE_POOL, OFFLINE_IDLE_POOL, BROADCAST_ANTICIPATION_POOL, COMMUNE_IDLE_POOL } from './pool'
import { streamText, createStreamTarget } from './streaming'
import { load } from '../core/save'
import { getNetworkState } from '../core/network'
import { broadcastDate } from '../core/seed'
import type { CursorController } from './cursor'

export interface IdleScheduler {
  start: () => void
  stop: () => void
}

// Poisson-distributed interval with clamping
function nextInterval(): number {
  const u = Math.random()
  const raw = -IDLE_MEAN_S * Math.log(1 - u)
  return Math.max(IDLE_MIN_S, Math.min(IDLE_MAX_S, raw))
}

// Seed the random so same visitor gets same chatter pattern
function seededRandom(seed: number): () => number {
  let s = seed
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff
    return s / 0x7fffffff
  }
}

export function mountIdleScheduler(
  history: HTMLElement,
  _cursor: CursorController,
): IdleScheduler {
  let timerId: ReturnType<typeof setTimeout> | null = null
  let lastLineIdx = -1
  let probablyCount = 0
  let lineCount = 0
  const reduced = () => document.body.classList.contains('vt-reduced-motion')

  // use save.firstSeenAt as phase seed for deterministic-ish chatter
  const s = load()
  const rand = seededRandom(s.firstSeenAt)

  function scheduleNext(): void {
    if (timerId !== null) return
    const interval = nextInterval()
    timerId = setTimeout(emitLine, interval * 1000)
  }

  async function emitLine(): Promise<void> {
    timerId = null

    // build active pool: normal + offline lines when offline
    const isOffline = getNetworkState() === 'offline'
    const timing = broadcastDate()
    const nearBroadcast = timing.msUntilNext > 0 && timing.msUntilNext <= 3600000

    const pool = isOffline
      ? [...IDLE_POOL, ...OFFLINE_IDLE_POOL]
      : nearBroadcast
        ? [...IDLE_POOL, ...BROADCAST_ANTICIPATION_POOL]
        : s.communeLog && s.communeLog.length > 0
          ? [...IDLE_POOL, ...COMMUNE_IDLE_POOL]
          : IDLE_POOL

    // pick random line, no immediate repeats, probably. capped
    let idx: number
    do {
      idx = Math.floor(rand() * pool.length)
    } while (idx === lastLineIdx && pool.length > 1)

    const line = pool[idx]
    lastLineIdx = idx
    lineCount++

    // track probably. appearances
    if (line === 'probably.') {
      probablyCount++
    } else {
      probablyCount = 0
    }

    // probably. must not appear twice in a row and <= 1 per 10 lines
    if (line === 'probably.' && (probablyCount > 1 || lineCount % 10 !== 0)) {
      // skip this one, pick another
      scheduleNext()
      return
    }

    const target = createStreamTarget(history, 'vt-line-idle')
    target.style.color = 'var(--vt-green-dim)'

    // don't move cursor for idle chatter — it stays at the prompt
    if (reduced()) {
      target.textContent = line
    } else {
      await streamText(target, line, { sfx: false })
    }

    scheduleNext()
  }

  return {
    start() {
      scheduleNext()
    },
    stop() {
      if (timerId !== null) {
        clearTimeout(timerId)
        timerId = null
      }
    },
  }
}
